"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Beer, Menu, X } from "lucide-react"
import { TIP_LABEL, TIP_REASON } from "./tip-link"
import { DaashLogo } from "@/components/daash-logo"
import { MARKETING_HOME_PATH, MARKETING_OUTER } from "@/lib/marketing-layout"
import { CONNECT_HREF, TIP_URL } from "@/lib/connector"

const PRESS = "[@media(hover:none)]:active:opacity-60"

const links = [
  { href: "/training-plans", label: "Plans" },
  { href: "/learn", label: "Learn" },
  { href: "/how-it-works", label: "How it works" },
  { href: "/about", label: "About" },
] as const

/** Plans · Learn · How it works · About, and the one red pill. Below 760px the links fold into a list under the bar. */
export function MarketingNav() {
  const [open, setOpen] = useState(false)
  const pathname = usePathname()

  // A new page closes the menu.
  useEffect(() => setOpen(false), [pathname])

  const isCurrent = (href: string) => pathname === href || pathname.startsWith(`${href}/`) || (href === "/learn" && (pathname === "/garmin-mcp-server" || pathname.startsWith("/connect-garmin-to-")))

  return (
    <header className="sticky top-0 z-50 border-b border-[#E6E8EC] bg-[#F7F8FA]/90 backdrop-blur-md">
      <div className={`flex h-14 items-center gap-6 ${MARKETING_OUTER}`}>
        <Link href={MARKETING_HOME_PATH} className="flex shrink-0 items-center rounded-md focus-visible:outline-2 focus-visible:outline-[#E0312A]">
          <DaashLogo />
        </Link>

        <nav className="hidden items-center gap-1 text-[14.5px] min-[760px]:flex" aria-label="Main">
          {links.map(({ href, label }) => (
            <Link
              key={href}
              href={href}
              aria-current={isCurrent(href) ? "page" : undefined}
              className={`rounded-lg px-2.5 py-2 no-underline hover:text-[#161B26] ${isCurrent(href) ? "font-semibold text-[#161B26]" : "text-[#5B6270]"}`}
            >
              {label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-1.5">
          <Link
            href={CONNECT_HREF}
            className={`inline-flex h-10 items-center rounded-full bg-[#E0312A] px-4 text-[15px] font-medium text-white no-underline hover:bg-[#C8261F] ${PRESS}`}
          >
            Connect
          </Link>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls="site-menu"
            aria-label={open ? "Close menu" : "Menu"}
            className={`flex size-11 items-center justify-center rounded-full text-[#161B26] min-[760px]:hidden ${PRESS}`}
          >
            {open ? <X className="size-[22px]" aria-hidden /> : <Menu className="size-[22px]" aria-hidden />}
          </button>
        </div>
      </div>

      {open && (
        <nav id="site-menu" className={`flex flex-col border-t border-[#E6E8EC] pb-4 pt-2 min-[760px]:hidden ${MARKETING_OUTER}`} aria-label="Main">
          {links.map(({ href, label }) => (
            <Link
              key={href}
              href={href}
              aria-current={isCurrent(href) ? "page" : undefined}
              className={`border-b border-[#EEF0F3] py-3 text-[17px] text-[#161B26] no-underline ${isCurrent(href) ? "font-semibold" : ""}`}
            >
              {label}
            </Link>
          ))}
          {/* The ask carries its reason: a bare ask for money in a menu reads as a paywall. */}
          {TIP_URL && (
            <a href={TIP_URL} target="_blank" rel="noopener" className="flex items-start gap-2.5 pt-3.5 text-[#161B26] no-underline">
              <Beer className="mt-0.5 size-4 shrink-0 text-[#C8261F]" aria-hidden />
              <span className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">{TIP_LABEL}</span>
                <span className="text-[13px] leading-snug text-[#5B6270]">{TIP_REASON}</span>
              </span>
            </a>
          )}
        </nav>
      )}
    </header>
  )
}
